import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spin, message, Button } from "antd";
import { ArrowLeftOutlined, HomeOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import Layout from "pages/HomePage/Layout";
import { apiGetHomepageData } from "services/dashboardService";
import { UpdateAppInfo } from "redux/actions/appActions";
import "./InfoPage.css";

const InfoPage = () => {
  const { categoryId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [category, setCategory] = useState(null);
  const [fixedHtml, setFixedHtml] = useState(null);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const iframeRef = useRef(null);

  // Get appInfo from Redux
  const appInfo = useSelector(state => state.appDataStore?.appInfo);

  // Load appInfo if not in Redux (e.g. direct page refresh)
  useEffect(() => {
    if (!appInfo) {
      let domain = window.location.hostname;
      if (domain === "localhost" || domain === "192.168.100.81") {
        domain = "demo.beclutch.club";
      }
      apiGetHomepageData(domain).then(res => {
        if (res.result) {
          dispatch(UpdateAppInfo({ appInfo: res.data }));
        } else {
          message.error("Failed to load page data");
          history.push("/");
        }
      });
    }
  }, []);

  useEffect(() => {
    if (appInfo) {
      loadCategoryData();
    }
  }, [categoryId, appInfo]);

  // Fetch fixed HTML file when category is set (cache-busted)
  useEffect(() => {
    if (category?.info_page_type === "fixed" && category?.info_page_file) {
      setLoadingHtml(true);
      const fileUrl = `/pages/${category.info_page_file}?t=${Date.now()}`;
      fetch(fileUrl)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then(html => {
          // Rewrite relative URLs to resolve from /pages/
          const rewritten = html
            .replace(/src="(?!http|\/|#)(.*?)"/g, 'src="/pages/$1"')
            .replace(/href="(?!http|\/|#|mailto)(.*?)"/g, 'href="/pages/$1"');
          setFixedHtml(rewritten);
          setLoadingHtml(false);
        })
        .catch(err => {
          console.error("[InfoPage] Failed to load HTML file:", err);
          setFixedHtml(null);
          setLoadingHtml(false);
        });
    }
  }, [category]);

  // Auto-resize iframe to fit content (no inner scrollbar)
  const resizeIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentDocument) {
      const body = iframe.contentDocument.body;
      if (body) {
        iframe.style.height = body.scrollHeight + "px";
      }
    }
  }, []);

  const loadCategoryData = () => {
    const foundCategory = appInfo?.homepage_categories?.find(
      cat => String(cat.id) === String(categoryId)
    );

    if (!foundCategory) {
      console.error("Category not found. Available categories:", appInfo?.homepage_categories?.map(c => c.id));
      message.error("Category not found");
      history.push("/");
      return;
    }

    setCategory(foundCategory);
  };

  const handleViewPrograms = () => {
    history.push(`/category/${categoryId}`);
  };

  const handleGoHome = () => {
    history.push("/");
  };

  if (!category) {
    return (
      <div style={{ textAlign: "center", padding: "100px 20px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout appInfo={appInfo}>
      <div className="info-page">
        <div className="info-header">
          <div className="header-actions">
            <button className="back-btn" onClick={() => history.goBack()} title="Go Back">
              <ArrowLeftOutlined />
            </button>
            <button className="home-btn" onClick={handleGoHome} title="Return to Home">
              <HomeOutlined />
            </button>
          </div>
          <h1 className="info-title">{category.title}</h1>
        </div>

        <div className="info-content-container">

          {/* Render content based on type */}
          {category.info_page_type === "fixed" && category.info_page_file ? (
            // Fixed type: render in auto-sized iframe for full style isolation
            <div className="info-fixed-content">
              {loadingHtml && (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin size="large" />
                </div>
              )}
              {fixedHtml && (
                <iframe
                  ref={iframeRef}
                  srcDoc={fixedHtml}
                  title={category.title}
                  className="info-iframe-auto"
                  frameBorder="0"
                  scrolling="no"
                  onLoad={resizeIframe}
                />
              )}
            </div>
          ) : (
            // Custom type: render rich text content
            <div
              className="info-content"
              dangerouslySetInnerHTML={{ __html: category.info_page_content || "" }}
            />
          )}

          {/* Bottom Call to Action Button */}
          <div className="info-cta">
            <Button
              type="primary"
              size="large"
              onClick={handleViewPrograms}
              className="view-programs-btn"
            >
              View Programs & Register
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InfoPage;
